import { ArrowDownTrayIcon, ArrowLeftCircleIcon, ArrowUpCircleIcon, CheckIcon, CurrencyDollarIcon, DevicePhoneMobileIcon, HomeIcon, LockClosedIcon, MapIcon, PresentationChartBarIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../assets/loaders/imageLoader";
import pdfs from "../../assets/loaders/pdfLoader";
import useScrollToTop from "../../hooks/useScrollToTop";
import { ModalContext } from "../../providers/ModalContext";
import { Paths } from "../../routes/paths";
import { RenderCardImage, renderCardInfo, RenderModalImage } from "./common";
import AnimateOnScroll from "../../components/common/miscellaneous/AnimateOnScroll";

const enum FixedTabs {
	TIPO_1 = "tipo1",
	TIPO_2 = "tipo2",
	TIPO_3 = "tipo3",
}

const JujuyPage = () => {
	const navigate = useNavigate()
	const { showScrollTop, scrollToTop } = useScrollToTop({ threshold: 200 })
	const [selectedTab, setSelectedTab] = useState(FixedTabs.TIPO_1)

	const galleryImages = useMemo(() => [
		{ src: images.jujuyRender1, alt: 'jujuy render 1' },
		{ src: images.jujuyRender2, alt: 'jujuy render 2' },
		{ src: images.jujuyRender3, alt: 'jujuy render 3' },
		{ src: images.jujuyRender4, alt: 'jujuy render 4' },
		{ src: images.jujuyRender5, alt: 'jujuy render 5' },
		{ src: images.jujuyRender6, alt: 'jujuy render 6' },
		{ src: images.jujuyPanoramica, alt: 'jujuy panorámica', style: { maxWidth: "100%", maxHeight: "40rem" } },
		{ src: images.jujuy1, alt: 'jujuy 1' },
		{ src: images.jujuy2, alt: 'jujuy 2' },
		{ src: images.jujuy3, alt: 'jujuy 3' },
		{ src: images.jujuy5, alt: 'jujuy 5' },
		{ src: images.jujuy4, alt: 'jujuy 4' },
	], []);

	const { openModal, closeModal } = useContext(ModalContext);

	const handleImageClick = useCallback((e: React.MouseEvent, imageSrc: string) => {
		openModal(<RenderModalImage images={galleryImages} initialImage={imageSrc} closeModal={closeModal} />,
			"p-0 bg-transparent shadow-none w-full max-w-full h-auto");
	}, [openModal, RenderModalImage]);

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [])

	return (
		<main id="unidades" className="min-h-screen bg-gradient-to-b from-gray-200 to-gray-210">
			<section className="relative h-[70vh] w-full" id="hero">
				<div className="absolute inset-0">
					<img src={images.jujuyPortrait} alt="Background" className="w-full h-full object-cover" />
				</div>
				<div className="absolute inset-0 bg-black/40 flex items-center justify-center lg:justify-start lg:ml-16">
					<img src={images.logoCompleteDark} alt="Logo" className="absolute top-4 left-4 w-48 sm:w-80" />
					<div className="absolute bottom-[12vh] sm:left-8 text-center text-white">
						<h1 className="text-4xl md:text-5xl font-bold mb-4 font-mono uppercase">Jujuy 006</h1>
						<p className="text-xl md:text-2xl max-w-2xl mx-auto font-mono">
							Una inversión de calidad
						</p>
					</div>
				</div>
			</section>

			<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
				<section className="px-4 md:px-12 py-8 md:py-12" id="caracteristicas">
					<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Características</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{renderCardInfo({
							icon: <HomeIcon className="w-6 mr-2 flex-shrink-0" />,
							title: "Ubicación",
							description: <>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Situado en una zona estratégica con acceso a principales vías y servicios esenciales.
								</li>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Todos los servicios.
								</li>
							</>
						})}
						{renderCardInfo({
							icon: <ShieldCheckIcon className="w-6 mr-2 flex-shrink-0" />,
							title: "Garantía de Calidad",
							description: <>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Construcción tradicional, materiales duraderos.
								</li>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Aberturas y revestimientos de primera calidad.
								</li>
							</>
						})}
						{renderCardInfo({
							icon: <PresentationChartBarIcon className="w-6 mr-2 flex-shrink-0" />,
							title: "Inversión asegurada",
							description: <>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Obtención de renta inmediata mediante alquiler.
								</li>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Revalorización del inmueble a largo plazo.
								</li>
							</>
						})}
						{renderCardInfo({
							icon: <CurrencyDollarIcon className="w-6 mr-2 flex-shrink-0" />,
							title: "Financiación",
							description: <>
								<li className="flex items-center gap-1">
									<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
									Financiación disponible: posibilidad de entrega y financiación.
								</li>
							</>
						})}
					</div>
				</section>
			</AnimateOnScroll>

			<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
				<section className="bg-white px-4 md:px-12 py-8 md:py-12" id="tipologias">
					<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Tipologías</h2>
					<div role="tablist" className="tabs tabs-md md:tabs-lg tabs-boxed ">
						<a role="tab" className={`tab font-mono font-semibold ${selectedTab === FixedTabs.TIPO_1 ? "tab-active" : ""}`}
							onClick={() => setSelectedTab(FixedTabs.TIPO_1)}>
							<span className="hidden sm:inline">1 Dormitorio</span>
							<span className="inline sm:hidden text-sm">1 Dorm</span>
						</a>
						<a role="tab" className={`tab font-mono font-semibold ${selectedTab === FixedTabs.TIPO_2 ? "tab-active" : ""}`}
							onClick={() => setSelectedTab(FixedTabs.TIPO_2)}>
							<span className="hidden sm:inline">1 Dormitorio Ampliado</span>
							<span className="inline sm:hidden text-sm">1 Dorm Ampl</span>
						</a>
						<a role="tab" className={`tab font-mono font-semibold ${selectedTab === FixedTabs.TIPO_3 ? "tab-active" : ""}`}
							onClick={() => setSelectedTab(FixedTabs.TIPO_3)}>
							<span className="hidden sm:inline">2 Dormitorios</span>
							<span className="inline sm:hidden text-sm">2 Dorm</span>
						</a>
					</div>
					<div role="tabpanel" className={`mt-8 transition-all duration-400 ${selectedTab === FixedTabs.TIPO_1 ? "opacity-100" : "opacity-0"}`}>
						<div className={`transition-all duration-400 ${selectedTab === FixedTabs.TIPO_1 ? "block" : "hidden"}`}>
							<TabContent1 />
						</div>
					</div>
					<div role="tabpanel" className={`mt-8 transition-all duration-400 ${selectedTab === FixedTabs.TIPO_2 ? "opacity-100" : "opacity-0"}`}>
						<div className={`transition-all duration-400 ${selectedTab === FixedTabs.TIPO_2 ? "block" : "hidden"}`}>
							<TabContent2 />
						</div>
					</div>
					<div role="tabpanel" className={`mt-8 transition-all duration-400 ${selectedTab === FixedTabs.TIPO_3 ? "opacity-100" : "opacity-0"}`}>
						<div className={`transition-all duration-400 ${selectedTab === FixedTabs.TIPO_3 ? "block" : "hidden"}`}>
							<TabContent3 />
						</div>
					</div>
				</section>
			</AnimateOnScroll>

			<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
				<section className="px-4 md:px-12 py-8 md:py-12" id="galeria">
					<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Galería de Imágenes</h2>
					<div className="flex flex-wrap justify-evenly gap-4">
						{galleryImages.map((img, index) => (
							<RenderCardImage
								key={'image-' + index}
								image={img.src}
								alt={img.alt}
								style={img.style}
								onImageClick={(e) => handleImageClick(e, img.src)}
							/>
						))}
					</div>
				</section>
			</AnimateOnScroll>

			<section className="px-4 md:px-12 py-8 md:py-12" id="cta">
				<div className="max-w-3xl mx-auto p-8 bg-base-200 rounded-xl shadow-lg">
					<h2 className="text-2xl font-bold mb-4">¿Interesado en conocer más?</h2>
					<p className="mb-6">
						Contáctenos. Queremos escucharte.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<button
							className="btn btn-outline flex items-center"
							onClick={() => window.open(`https://wa.me/5493433022876?text=Hola, quiero contactarme por el proyecto Jujuy 006.`)}
						>
							<DevicePhoneMobileIcon className="w-6 mr-2 flex-shrink-0" />
							Contactános
						</button>
						<button className="btn btn-outline" onClick={() => window.open(pdfs.jujuyPlanos, '_blank')}>
							<ArrowDownTrayIcon className="w-6 mr-2 flex-shrink-0" />
							Descargar Brochure
						</button>
						<button className="btn btn-outline" onClick={() => window.open("https://maps.app.goo.gl/V1dHMQewEbLYzL1r5", '_blank')}>
							<MapIcon className="w-6 mr-2 flex-shrink-0" />
							Ubicación
						</button>
					</div>
				</div>
			</section>

			<AnimatePresence>
				{showScrollTop && (
					<>
						<motion.button
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							onClick={scrollToTop}
							className={`fixed right-6 bottom-4 z-50 p-2 rounded-full 
            bg-ghost text-primary-content shadow-lg hover:bg-neutral transition-colors duration-200`}
							aria-label="Scroll to top"
						>
							<ArrowUpCircleIcon className="w-8 text-info" title="Volver arriba" />
						</motion.button>
						<motion.button
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							onClick={() => navigate(Paths.home)}
							className={`fixed left-6 bottom-4 z-50 p-2 rounded-full 
            bg-ghost text-primary-content shadow-lg hover:bg-neutral transition-colors duration-200`}
							aria-label="Scroll to top"
						>
							<ArrowLeftCircleIcon className="w-8 text-secondary" title="Volver atrás" />
						</motion.button>
					</>
				)}
			</AnimatePresence>
		</main>
	);
};

const TabContent1 = () => {
	return (
		<div className="p-4 flex flex-col md:flex-row justify-between gap-10">
			<div className="mx-auto">
				<h3 className="text-2xl font-bold mb-4 text-center">Apartamento de 1 Dormitorio</h3>
				<ul className="space-y-2 mb-6">
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Superficie total: 48 m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>1 Baño completo</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Cocina integrada</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Balcón privado de 4m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Living - Comedor</span>
					</li>
				</ul>
				{/* <p className="text-normal max-w-md">
					Ideal para solteros o parejas que buscan un espacio cómodo y funcional con todas las comodidades
					necesarias.
				</p> */}
			</div>
			<div>
				<img src={images.jujuyRender9} alt="Jujuy Render 9" className="w-[40rem]" />
			</div>
		</div>
	);
};

const TabContent2 = () => {
	return (
		<div className="p-4 flex flex-col md:flex-row justify-between gap-10">
			<div className="mx-auto">
				<h3 className="text-2xl font-bold mb-4 text-center">Apartamento de 1 Dormitorio Ampliado</h3>
				<ul className="space-y-2 mb-6">
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Superficie total: 60 m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>1 Baño completo</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Cocina integrada</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Balcón privado de 7m² o 9m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Gran Living - Comedor</span>
					</li>
				</ul>
			</div>
			<div>
				<img src={images.jujuyRender11} alt="Jujuy Render 11" className="w-[40rem]" />
			</div>
		</div>
	);
};

const TabContent3 = () => {
	return (
		<div className="p-4 flex flex-col md:flex-row justify-between gap-10">
			<div className="mx-auto">
				<h3 className="text-2xl font-bold mb-4 text-center">Apartamento de 2 Dormitorios</h3>
				<ul className="space-y-2 mb-6">
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Superficie total: 62 m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>1 Baño completo</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Cocina integrada</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Balcón privado de 6m²</span>
					</li>
					<li className="flex items-center gap-1">
						<CheckIcon className="w-5 mr-2 text-green-500 flex-shrink-0" />
						<span>Amplio Living - Comedor</span>
					</li>
				</ul>
			</div>
			<div>
				<img src={images.jujuyRender10} alt="Jujuy Render 10" className="w-[40rem]" />
			</div>
		</div>
	);
};

export default JujuyPage;
